import { useEffect, useRef, useState } from 'react'

import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import type { PendingToolCall, ShowOptionsResult } from '../core/engine/types'
import { theme } from '@/Atomic/assets/theme'

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space.m};
  padding: ${theme.space.m} ${theme.space.l};
  background: ${theme.colors.background.default};
  border: 1px solid ${theme.colors.border.stroke};
  border-radius: ${theme.radii.md};
  margin: ${theme.space.xs} 0;
`

const Question = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  color: ${theme.colors.text.primary};
`

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space.xs};
`

// Radio-style item
const RadioItem = styled.div<{ checked: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.space.s};
  padding: ${theme.space.s} ${theme.space.m};
  border-radius: ${theme.radii.sm};
  cursor: pointer;
  user-select: none;
  font-size: 13px;
  color: ${theme.colors.text.primary};
  transition: background 0.1s;

  &:hover {
    background: ${theme.colors.background.hover};
  }
`

const RadioDot = styled.span<{ checked: boolean }>`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid
    ${({ checked }) =>
      checked ? theme.colors.primary.main : theme.colors.border.strong};
  background: ${theme.colors.background.default};
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color 0.15s,
    background 0.15s;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ checked }) =>
      checked ? theme.colors.primary.main : 'transparent'};
    transition: background 0.15s;
  }
`

// Checkbox-style item
const CheckItem = styled.div<{ checked: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.space.s};
  padding: ${theme.space.s} ${theme.space.m};
  border-radius: ${theme.radii.sm};
  cursor: pointer;
  user-select: none;
  font-size: 13px;
  color: ${theme.colors.text.primary};
  transition: background 0.1s;

  &:hover {
    background: ${theme.colors.background.hover};
  }
`

const CheckBox = styled.span<{ checked: boolean }>`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 2px solid
    ${({ checked }) =>
      checked ? theme.colors.primary.main : theme.colors.border.strong};
  background: ${({ checked }) =>
    checked ? theme.colors.primary.main : theme.colors.background.default};
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color 0.15s,
    background 0.15s;

  svg {
    display: ${({ checked }) => (checked ? 'block' : 'none')};
  }
`

const DateInputRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${theme.space.m};
`

const DateInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space.xs};
  flex: 1;
`

const DateLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${theme.colors.text.secondary};
`

const DateInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: ${theme.space.s} ${theme.space.m};
  border-radius: ${theme.radii.sm};
  border: 1px solid ${theme.colors.border.stroke};
  font-size: 13px;
  font-family: ${theme.fontFamilies.body};
  color: ${theme.colors.text.primary};
  background: ${theme.colors.background.default};
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
  }

  &:hover:not(:focus) {
    border-color: ${theme.colors.border.active};
  }
`

const Actions = styled.div`
  display: flex;
  gap: ${theme.space.s};
  justify-content: flex-end;
  padding-top: ${theme.space.xs};
  border-top: 1px solid ${theme.colors.border.line};
`

const Btn = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: ${theme.space.xs} ${theme.space.m};
  border-radius: ${theme.radii.sm};
  font-size: 13px;
  font-weight: 500;
  font-family: ${theme.fontFamilies.body};
  cursor: pointer;
  border: 1px solid
    ${({ variant }) =>
      variant === 'primary'
        ? theme.colors.primary.main
        : theme.colors.border.stroke};
  background: ${({ variant }) =>
    variant === 'primary'
      ? theme.colors.primary.main
      : theme.colors.background.default};
  color: ${({ variant }) =>
    variant === 'primary' ? '#fff' : theme.colors.text.primary};
  transition:
    background 0.15s,
    opacity 0.15s;

  &:hover:not(:disabled) {
    background: ${({ variant }) =>
      variant === 'primary'
        ? theme.colors.primary.hover
        : theme.colors.background.hover};
    border-color: ${({ variant }) =>
      variant === 'primary'
        ? theme.colors.primary.hover
        : theme.colors.border.active};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

interface OptionsCardProps {
  pending: Extract<PendingToolCall, { type: 'options' | 'dateRange' }>
  multiSelect?: boolean
  onSubmit: (result: ShowOptionsResult) => void
}

export function OptionsCard({
  pending,
  multiSelect = false,
  onSubmit,
}: OptionsCardProps) {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<string[]>(() => {
    if (pending.type !== 'options') {
      return []
    }
    if (pending.preselected) {
      return pending.preselected
    }
    // A confirm card with a single match preselects it — there is nothing to
    // choose between, so the user only has to confirm.
    const sole = pending.options.length === 1 ? pending.options[0] : undefined
    return sole ? [sole.value] : []
  })
  const [startDate, setStartDate] = useState<string>(
    pending.type === 'dateRange' ? (pending.defaultStartDate ?? '') : '',
  )
  const [endDate, setEndDate] = useState<string>(
    pending.type === 'dateRange' ? (pending.defaultEndDate ?? '') : '',
  )
  const [focusedIndex, setFocusedIndex] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  // Opt-in: an empty selection is a valid submit (e.g. the summarize section
  // picker, where "none" means summarize base info only). Every other picker
  // keeps the "must pick at least one" gate.
  const allowEmpty = pending.type === 'options' && pending.allowEmpty === true

  useEffect(() => {
    ;(listRef.current?.children[0] as HTMLElement | undefined)?.focus()
  }, [])

  function toggle(option: string) {
    if (multiSelect) {
      setSelected((prev) =>
        prev.includes(option)
          ? prev.filter((o) => o !== option)
          : [...prev, option],
      )
    } else {
      setSelected((prev) => (prev[0] === option ? [] : [option]))
    }
  }

  function handleOptionKeyDown(
    e: React.KeyboardEvent<HTMLDivElement>,
    option: string,
    index: number,
    total: number,
  ) {
    if (e.key === ' ') {
      e.preventDefault()
      toggle(option)
    }
    if (e.key === 'Enter' && (allowEmpty || selected.length > 0)) {
      e.preventDefault()
      onSubmit({ selected, cancelled: false })
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault()
      const nextIndex = (index + 1) % total
      setFocusedIndex(nextIndex)
      const next = listRef.current?.children[nextIndex] as
        | HTMLElement
        | undefined
      next?.focus()
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault()
      const prevIndex = (index - 1 + total) % total
      setFocusedIndex(prevIndex)
      const prev = listRef.current?.children[prevIndex] as
        | HTMLElement
        | undefined
      prev?.focus()
    }
  }

  if (pending.type === 'dateRange') {
    return (
      <Card>
        <Question>{pending.question}</Question>
        <DateInputRow>
          <DateInputGroup>
            <DateLabel>
              {t('components.aiChatBot.dateRange.startDate')}
            </DateLabel>
            <DateInput
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </DateInputGroup>
          <DateInputGroup>
            <DateLabel>{t('components.aiChatBot.dateRange.endDate')}</DateLabel>
            <DateInput
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </DateInputGroup>
        </DateInputRow>
        <Actions>
          <Btn
            variant="secondary"
            type="button"
            onClick={() =>
              onSubmit({ startDate: '', endDate: '', cancelled: true })
            }
          >
            {t('components.aiChatBot.optionsCard.cancel')}
          </Btn>
          <Btn
            variant="primary"
            type="button"
            disabled={!startDate || !endDate}
            onClick={() => onSubmit({ startDate, endDate, cancelled: false })}
          >
            {t('components.aiChatBot.optionsCard.confirm')}
          </Btn>
        </Actions>
      </Card>
    )
  }

  const sortedOptions = multiSelect
    ? [...pending.options].sort((a, b) => a.label.localeCompare(b.label))
    : pending.options

  return (
    <Card>
      <Question>{pending.question}</Question>
      <OptionsList ref={listRef} role={multiSelect ? 'group' : 'radiogroup'}>
        {sortedOptions.map((option, index) => {
          const checked = selected.includes(option.value)
          if (multiSelect) {
            return (
              <CheckItem
                key={`${option.value}:${index}`}
                checked={checked}
                role="checkbox"
                aria-checked={checked}
                tabIndex={0}
                onClick={() => toggle(option.value)}
                onKeyDown={(e) =>
                  handleOptionKeyDown(
                    e,
                    option.value,
                    index,
                    sortedOptions.length,
                  )
                }
              >
                <CheckBox checked={checked} aria-hidden="true">
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path
                      d="M1 4L3.5 6.5L9 1"
                      stroke="white"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </CheckBox>
                {option.label}
              </CheckItem>
            )
          }
          return (
            <RadioItem
              key={`${option.value}:${index}`}
              checked={checked}
              role="radio"
              aria-checked={checked}
              tabIndex={index === focusedIndex ? 0 : -1}
              onClick={() => {
                toggle(option.value)
                setFocusedIndex(index)
              }}
              onKeyDown={(e) =>
                handleOptionKeyDown(
                  e,
                  option.value,
                  index,
                  sortedOptions.length,
                )
              }
            >
              <RadioDot checked={checked} aria-hidden="true" />
              {option.label}
            </RadioItem>
          )
        })}
      </OptionsList>
      <Actions>
        <Btn
          variant="secondary"
          type="button"
          onClick={() => onSubmit({ selected: [], cancelled: true })}
        >
          {t('components.aiChatBot.optionsCard.cancel')}
        </Btn>
        <Btn
          variant="primary"
          type="button"
          disabled={!allowEmpty && selected.length === 0}
          onClick={() => onSubmit({ selected, cancelled: false })}
        >
          {t('components.aiChatBot.optionsCard.confirm')}
        </Btn>
      </Actions>
    </Card>
  )
}
